import React, { useState } from "react";
import { Modal, Form, Input, Select, Button, message, Tooltip } from "antd";
import { FaExternalLinkAlt, FaInfoCircle, FaCopy, FaEye, FaEyeSlash, FaEnvelope } from "react-icons/fa";
import { api } from "../../config/api";

const { Option } = Select;

const ExternalUrlModal = ({ isVisible, onClose, onSuccess }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [generatedLink, setGeneratedLink] = useState("");
    const [generatedPassword, setGeneratedPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const generateRandomPassword = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let password = '';
        for (let i = 0; i < 12; i++) {
            password += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return password;
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();

            const response = await api.post("/external-links/create", values);

            if (response.data?.success) {
                setGeneratedLink(`${window.location.origin}/guest-estimate/${response.data.linkId}`);
                setGeneratedPassword(values.password);
                message.success("External link created successfully!");
                if (onSuccess) onSuccess();
            } else {
                message.error("Failed to create external link");
            }
        } catch (error) {
            console.error("Error creating external link:", error);
            message.error("An error occurred while creating the external link");
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text, label) => {
        navigator.clipboard.writeText(text);
        message.success(`${label} copied to clipboard!`);
    };

    const getShareMessage = (linkName, url, password) => {
        return `External User Estimate Link - ${linkName}

You've been invited to create an estimate with One Union Solutions!

Access Details:
• Estimation URL: ${url}
• Access Password: ${password}

How to access:
1. Click the link above or copy and paste it in your browser
2. Enter the password when prompted
3. Complete your estimation form securely

This is a secure, password-protected estimation One Union Solutions. Please keep your access credentials safe.

Best regards,
One Union Solutions Team

#Estimation #SecureAccess #OneUnionSolutions`;
    };

    const handleCancel = () => {
        form.resetFields();
        setGeneratedLink("");
        setGeneratedPassword("");
        setShowPassword(false);
        onClose();
    };

    const handleGeneratePassword = () => {
        const newPassword = generateRandomPassword();
        form.setFieldsValue({ password: newPassword });
        setShowPassword(true); // Show password after generation
        message.success("Password generated successfully!");
    };

    return (
        <Modal
            title={
                <div className="flex items-center gap-2 text-[#03336e]">
                    <FaExternalLinkAlt />
                    <span>Create External Estimation URL</span>
                </div>
            }
            open={isVisible}
            onCancel={handleCancel}
            footer={null}
            width={600}
            centered
        >
            <div className="mb-6 p-4 bg-blue-50 border-l-4 border-[#03336e] rounded">
                <div className="flex items-start gap-3">
                    <FaInfoCircle className="text-[#03336e] text-xl mt-1" />
                    <div>
                        <h3 className="font-bold text-base mb-1">Secure Guest Estimate Link</h3>
                        <p className="text-gray-700">
                            Create a password-protected estimation URL for users who are not One Union Solutions clients.
                            This link allows external users to create estimates with secure access control.
                        </p>
                    </div>
                </div>
            </div>

            {!generatedLink ? (
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        expiryTime: "7",
                        maxEstimates: "1"
                    }}
                >
                    <Form.Item
                        name="linkName"
                        label="Link Name"
                        rules={[{ required: true, message: "Please enter a name for this link" }]}
                    >
                        <Input placeholder="E.g., Client ABC Estimate" />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label="Recipient Email"
                        rules={[
                            { required: true, message: "Please enter recipient email" },
                            { type: "email", message: "Please enter a valid email address" }
                        ]}
                    >
                        <Input placeholder="E.g., client@example.com" />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        label="Access Password"
                        extra="Users will need this password to access the estimation form"
                        rules={[
                            { required: true, message: "Please enter a password" },
                            { min: 6, message: "Password must be at least 6 characters long" }
                        ]}
                    >
                        <div className="flex gap-2">
                            <Input.Password
                                placeholder="Enter password for link access"
                                visibilityToggle={{
                                    visible: showPassword,
                                    onVisibleChange: setShowPassword,
                                }}
                            />
                            <Button
                                onClick={handleGeneratePassword}
                                className="flex-shrink-0"
                            >
                                Generate
                            </Button>
                        </div>
                    </Form.Item>

                    <Form.Item
                        name="expiryTime"
                        label="Link Expiry Time"
                        rules={[{ required: true, message: "Please select expiry time" }]}
                    >
                        <Select placeholder="Select expiry time">
                            <Option value="1">1 Day</Option>
                            <Option value="3">3 Days</Option>
                            <Option value="7">7 Days</Option>
                            <Option value="14">14 Days</Option>
                            <Option value="30">30 Days</Option>
                            <Option value="90">90 Days</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="maxEstimates"
                        label="Maximum Estimates"
                        tooltip="Maximum number of estimates that can be created using this link"
                        rules={[{ required: true, message: "Please select maximum estimates" }]}
                    >
                        <Select placeholder="Select maximum estimates">
                            <Option value="1">1 Estimate</Option>
                            <Option value="3">3 Estimates</Option>
                            <Option value="5">5 Estimates</Option>
                            <Option value="10">10 Estimates</Option>
                            <Option value="unlimited">Unlimited</Option>
                        </Select>
                    </Form.Item>

                    <div className="flex justify-end gap-3 mt-6">
                        <Button onClick={handleCancel}>Cancel</Button>
                        <Button
                            type="primary"
                            onClick={handleSubmit}
                            loading={loading}
                            className="bg-[#03336e]"
                        >
                            Generate Link
                        </Button>
                    </div>
                </Form>
            ) : (
                <div className="mt-4 space-y-4">
                    <div className="border rounded-lg p-4 bg-gray-50">
                        <div className="mb-2 font-medium text-gray-700">Generated Link:</div>
                        <div className="flex items-center">
                            <div className="truncate flex-grow border rounded-l px-3 py-2 bg-white">
                                {generatedLink}
                            </div>
                            <Button
                                onClick={() => copyToClipboard(generatedLink, "Link")}
                                className="rounded-none border-l-0 flex items-center justify-center"
                                icon={<FaCopy />}
                            />
                        </div>
                    </div>

                    <div className="border rounded-lg p-4 bg-red-50 border-red-200">
                        <div className="mb-2 font-medium text-red-700 flex items-center gap-2">
                            <FaInfoCircle />
                            Access Password:
                        </div>
                        <div className="flex items-center">
                            <div className="flex-grow border rounded-l px-3 py-2 bg-white font-mono">
                                {showPassword ? generatedPassword : '•'.repeat(generatedPassword.length)}
                            </div>
                            <Button
                                onClick={() => setShowPassword(!showPassword)}
                                className="border-l-0 border-r-0 flex items-center justify-center"
                                icon={showPassword ? <FaEyeSlash /> : <FaEye />}
                            />
                            <Button
                                onClick={() => copyToClipboard(generatedPassword, "Password")}
                                className="rounded-l-none border-l-0 flex items-center justify-center"
                                icon={<FaCopy />}
                            />
                        </div>
                        <div className="mt-2 text-sm text-red-600">
                            ⚠️ Save this password securely. Users will need it to access the estimation form.
                        </div>
                    </div>

                    <div className="mt-4">
                        <h3 className="font-medium mb-2">Share this link</h3>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                onClick={() => {
                                    const linkName = form.getFieldValue('linkName') || 'Estimation';
                                    const shareMessage = getShareMessage(linkName, generatedLink, generatedPassword);
                                    copyToClipboard(shareMessage, "Full Message");
                                }}
                                icon={<FaCopy />}
                            >
                                Copy Full Message
                            </Button>
                            <Button
                                onClick={() => {
                                    const linkName = form.getFieldValue('linkName') || 'Estimation';
                                    const email = form.getFieldValue('email') || '';
                                    const shareMessage = getShareMessage(linkName, generatedLink, generatedPassword);
                                    const mailtoUrl = `mailto:${email}?subject=${encodeURIComponent(`External User Estimate Link - ${linkName}`)}&body=${encodeURIComponent(shareMessage)}`;
                                    window.open(mailtoUrl, '_blank');
                                }}
                                icon={<FaEnvelope />}
                            >
                                Share via Email
                            </Button>
                        </div>
                    </div>

                    <div className="mt-6 flex justify-end gap-3">
                        <Button onClick={handleCancel}>Close</Button>
                        <Button
                            type="primary"
                            onClick={() => {
                                form.resetFields();
                                setGeneratedLink("");
                                setGeneratedPassword("");
                                setShowPassword(false);
                            }}
                            className="bg-[#03336e]"
                        >
                            Create Another Link
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default ExternalUrlModal;